import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Dropdown, DropdownItem} from '@homeaway/react-dropdown';
import CurrencyMenuItem from '../CurrencyMenuItem/CurrencyMenuItem';
import {triggerCurrencySelectorPresented, triggerCurrencySelectorSelected, triggerTopCurrencyPresented, triggerViewAllCurrencySelected, triggerAllCurrencyPresented, triggerTopCurrencySelected, triggerAllCurrencySelected} from '../../utils/edapUtils';
import {Modal} from '@homeaway/react-modal';
import './CurrencySwitcher.less';
import {SVGIcon} from '@homeaway/react-svg';
import {CHEVRON_RIGHT__16, MONEY__24} from '@homeaway/svg-defs';

class CurrencySwitcher extends Component {
    static propTypes = {
        /**
         * @prop {*} currencyCode : contains the ISO code of the currency to be displayed in the dropdown
         */
        currencyCode: PropTypes.string,
        currencyName: PropTypes.string,
        currencySymbol: PropTypes.string,
        renderCurrencySelector: PropTypes.bool,
        currencySelector: PropTypes.shape({
            currencies: PropTypes.arrayOf(
                PropTypes.shape({
                    code: PropTypes.string,
                    symbol: PropTypes.string,
                    name: PropTypes.string
                })
            )
        }),
        popularCurrencies: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                symbol: PropTypes.string,
                name: PropTypes.string
            })
        ),
        translations: PropTypes.shape({
            showAllCurrenciesText: PropTypes.string,
            selectCurrencyTitle: PropTypes.string
        }),
        /**
         * @prop {*} onCurrencySelectorChange : passed from CurrencySelectorConnector component in react-site-header.
         * @argument {*} currency: currency code of the selected currency to be passed from CurrencySwitcher
         */
        onCurrencySelectorChange: PropTypes.func,
        isMobile: PropTypes.bool
    };

    static defaultProps = {
        currencyCode: 'USD',
        currencyName: 'US Dollar',
        currencySymbol: '$',
        renderCurrencySelector: true,
        popularCurrencies: [
            {code: 'USD', symbol: '$', name: 'US Dollar'},
            {code: 'EUR', symbol: '€', name: 'Euro'},
            {code: 'GBP', symbol: '£', name: 'British Pound Sterling'}
        ],
        translations: {
            showAllCurrenciesText: 'Show all currencies',
            selectCurrencyTitle: 'Select currency'
        },
        onCurrencySelectorChange: () => {},
        isMobile: false
    };

    constructor(props) {
        super(props);
        this.state = this.getStateDataByProps(this.props) || {};
        this.state.showAllCurrencies = false;

        this.handleCurrencySelectorChange = this.handleCurrencySelectorChange.bind(this);
        this.handleAllCurrenciesClick = this.handleAllCurrenciesClick.bind(this);
        this.handleAllCurrenciesClose = this.handleAllCurrenciesClose.bind(this);
        this.handleDropdownClick = this.handleDropdownClick.bind(this);
        this.triggerTopCurrencySelectedEvent = this.triggerTopCurrencySelectedEvent.bind(this);
        this.triggerAllCurrencySelectedEvent = this.triggerAllCurrencySelectedEvent.bind(this);
        this.cspTriggered = false; // Currency Selector Presented triggered

        if (!this.props.isMobile && this.state.renderCurrencySelector && !this.cspTriggered) {
            triggerCurrencySelectorPresented(this.props.currencyCode);
            this.cspTriggered = true;
        }

        this.popularCurrencyData = this.props.currencySelector ? this.getPopularCurrencyData(this.props.currencySelector.currencies[0], this.props.popularCurrencies) : [];
    }

    componentWillReceiveProps(nextProps) {
        this.setState(this.getStateDataByProps(nextProps));

        if (!this.props.isMobile && this.state.renderCurrencySelector && !this.cspTriggered) {
            triggerCurrencySelectorPresented(this.props.currencyCode);
            this.cspTriggered = true;
        }
    }

    getCurrencyObjFromCurrencyCode(currencyList, currencyCode) {
        if (!currencyList) {
            return null;
        }
        return currencyList.filter((currency) => currencyCode === currency.code)[0];
    }
    /**
     *
     * @param {*} defaultSiteCurrency : default currency of the site 
     * @param {*} popularCurrencies : list of popular currencies
     * This function iterates over the list of popular currencies and checks whether the currently selected
     * currency/site default currency is present there. If not, the currently selected currency/site default currency
     * is pushed to the top of the popularCurrencies array
     */
    getPopularCurrencyData(defaultSiteCurrency, popularCurrencies) {
        let selectedCurrencyInList = popularCurrencies.filter((currency) => {
            return currency.code === defaultSiteCurrency.code;
        });
        if (!selectedCurrencyInList.length) {
            popularCurrencies.unshift(defaultSiteCurrency);
        } else {
            let index = popularCurrencies.findIndex((currency) => {
                return currency.code === defaultSiteCurrency.code;
            });
            if (index > 0) {
                popularCurrencies.splice(index, 1);
                popularCurrencies.unshift(defaultSiteCurrency);
            }
        }
        return popularCurrencies;
    }

    getStateDataByProps(props) {
        if (!props.currencySelector) {
            return null;
        }
        this.popularCurrencyData = this.getPopularCurrencyData(props.currencySelector.currencies[0], props.popularCurrencies);
        let currencyObj = this.getCurrencyObjFromCurrencyCode(props.currencySelector.currencies, props.currencyCode);
        return {
            selectedCurrency: currencyObj,
            renderCurrencySelector: props.renderCurrencySelector && !!props.currencySelector && !!currencyObj,
        };
    }

    handleCurrencySelectorChange(e) {
        this.setState({
            selectedCurrency: this.getCurrencyObjFromCurrencyCode(this.props.currencySelector.currencies, e.currentTarget.dataset.currencyCode),
            showAllCurrencies: false
        });
        this.props.onCurrencySelectorChange(e.currentTarget.dataset.currencyCode);
    }
    
    handleAllCurrenciesClick() {
        this.setState({
            showAllCurrencies: true
        });

        triggerViewAllCurrencySelected(this.state.selectedCurrency.code);
        triggerAllCurrencyPresented(this.state.selectedCurrency.code);
    }

    handleAllCurrenciesClose() {
        this.setState({
            showAllCurrencies: false
        });
    }

    handleDropdownClick() {
        triggerCurrencySelectorSelected(this.state.selectedCurrency.code);
        triggerTopCurrencyPresented(this.popularCurrencyData.map((currency) => currency.code).join(','), this.state.selectedCurrency.code);
    }

    getSelectedCurrencyDisplay() {
        return (
            <span>
                <SVGIcon className="CurrencySwitcher__currency-sign" markup={MONEY__24} />
                <span className="CurrencySwitcher__toggle-currency-code">
                    {this.state.selectedCurrency && this.state.selectedCurrency.code.toUpperCase()}
                </span>
                <span className="CurrencySwitcher__toggle-currency-code">({this.state.selectedCurrency && this.state.selectedCurrency.symbol})
                </span>
            </span>
        );
    }

    triggerTopCurrencySelectedEvent(currencyCode) {
        triggerTopCurrencySelected(currencyCode, this.state.selectedCurrency.code);
    }

    triggerAllCurrencySelectedEvent(currencyCode) {
        triggerAllCurrencySelected(currencyCode, this.state.selectedCurrency.code);
    }

    render() {
        if (!this.state.renderCurrencySelector) {
            return null;
        }
        const {translations: {selectCurrencyTitle, showAllCurrenciesText}, currencySelector: {currencies}} = this.props;
        return (
            <div className="CurrencySwitcher">
                <Dropdown
                    className="visible-lg visible-xl-block"
                    dropdownRight
                    label="Dropdown"
                    srOnlyLabel
                    id="currency-selector-dropdown"
                    closeAfterContentClick
                    customToggle={
                        <button className="CurrencySwitcher__toggle">
                            {this.getSelectedCurrencyDisplay()}
                        </button>
                    }
                    onDropdownShow={this.handleDropdownClick}
                >
                    {this.popularCurrencyData.map((currency) => {
                        return (
                            <DropdownItem
                                link="#"
                                text={
                                    <CurrencyMenuItem
                                        currencyCode={currency.code}
                                        currencySymbol={currency.symbol}
                                        currencyName={currency.name}
                                        isActive={this.state.selectedCurrency.code === currency.code}
                                        eventTrigger={this.triggerTopCurrencySelectedEvent}
                                    />
                                }
                                key={currency.code}
                                data-currency-code={currency.code}
                                onClick={this.handleCurrencySelectorChange}
                            />
                        );
                    })}
                    <DropdownItem
                        link="#"
                        text={
                            <div
                                className="CurrencySwitcher__show-all-link"
                                role={'menuitem'}
                            >
                                {showAllCurrenciesText}
                            </div>
                        }
                        onClick={this.handleAllCurrenciesClick}
                    />
                </Dropdown>
                <a className="hidden-lg hidden-xl CurrencySwitcher--MobileWeb" href="#">
                    {this.getSelectedCurrencyDisplay()}
                    <SVGIcon className="text-muted" inline markup={CHEVRON_RIGHT__16} />
                </a>
                <Modal
                    id="all-currencies-modal"
                    isOpen={this.state.showAllCurrencies}
                    closeButton={true}
                    onClose={this.handleAllCurrenciesClose}
                    title={selectCurrencyTitle}
                >

                    <div className="all-currencies-container">
                        {currencies.map((currency) => {
                            return (
                                <div className="currency-item" data-currency-code={currency.code} onClick={this.handleCurrencySelectorChange} key={currency.code}>
                                    <CurrencyMenuItem
                                        currencyCode={currency.code}
                                        currencySymbol={currency.symbol}
                                        currencyName={currency.name}
                                        isActive={this.state.selectedCurrency.code === currency.code}
                                        eventTrigger={this.triggerAllCurrencySelectedEvent}
                                    />
                                </div>
                            );
                        })}
                    </div>

                </Modal>
            </div>
        );
    }
}

export default CurrencySwitcher;
